import classNames from "classnames";
import React, { ReactElement, useCallback, useEffect, useState } from "react";
import { DashboardChecksTypeEnum, FileTypeAdminEnum, FileTypeEnum, fileApis, uploadApis, userApis } from "@payping/api";
import type { UserDocumentAdminModel } from "@payping/api";
import { dateFormats, toJalali, toUtc } from "@payping/utils/dateUtils";
import { getFileName, getFileType } from "@payping/utils/string";
import { buildUrl } from "@payping/utilities-shared/url";
import { getUserBusinessinfo, getUserChecks, updateBusinessinfo } from "@payping/redux/actions";
import { Close, InfoCircleOutlined, Upload as UploadIcon } from "@payping/icons";
import { useLocation, useNavigate } from "react-router-dom";
import { Trans } from "@payping/utils/localeUtils";
import type { UploadChangeParam, UploadFile } from "@payping/web-components/components/upload/types";
import { useAsyncRetry } from "react-use";
import { ValidLocalPhoneNumber, ValidPostalCode, ValidUrl } from "@payping/utils/validation";
import { DashboardRoutes } from "@/router/constant";
import {
  Button,
  Col,
  Drawer,
  Input,
  InputNumber,
  LegacyDatePicker,
  LegacyForm,
  Modal,
  Page,
  Row,
  Select,
  Space,
  Typography,
  Upload,
  useMessagedAsync,
  useMessagedAsyncFn,
} from "@payping/web-components";

import { useAppDispatch, useAppSelector } from "@/utils/redux";
import { FormLayout } from "@/components/DashUser/FormLayout";
import location from "@/assets/location.json";
import { SubLayout } from "@/components/SubLayout";
import { useAppTranslation } from "@/utils/useAppTranslation";
import { useLastUrlWasPage } from "@/utils";
import { SwitchRow } from "@/core/component";
import { URLS } from "@payping/utilities-shared/consts";
import styles from "../../../assets/scss/profile/Profile.module.scss";
import { BusinessInformation } from "./type";

const { Option } = Select;
interface CityType {
  cityName: string;
  cityId: number;
}

const BusinessEdit: React.FC = () => {
  // =========================================
  // Init
  // =========================================
  const { settings } = DashboardRoutes;
  const dispatch = useAppDispatch();
  const { t } = useAppTranslation();
  const [form] = LegacyForm.useForm<BusinessInformation>();
  const [modalVisible, setModalVisible] = useState<boolean>(false);
  const [businessCertificateList, setBusinessCertificateList] = useState<UploadFile[]>([]);
  const [introductionLetterList, setIntroductionLetterList] = useState<UploadFile[]>([]);
  const [permissionList, setPermissionList] = useState<UploadFile[]>([]);
  const [removeDocCalled, setRemoveDocCalled] = useState<boolean>(false);
  const [cities, setCities] = useState<Array<CityType>>();
  const navigate = useNavigate();
  const currentUrl = useLocation().pathname;
  const isRegisterURL = currentUrl === DashboardRoutes.register.business.url;
  const isLocalDisabled = useAppSelector(
    (state) => state.user.checks?.localStatus === DashboardChecksTypeEnum.Confirmed,
  );
  const localInfo = useAppSelector((state) => state.user.localInfo);
  const checks = useAppSelector((state) => state.user.checks);
  // TODO: this logic should be checked in backend and included inside checks.businessStatus (remove this section when new User service is launched)
  const isBusinessDisabled = checks?.businessStatus === DashboardChecksTypeEnum.Confirmed;
  const isSabtDisabled = checks?.businessRegistrationStatus === DashboardChecksTypeEnum.Confirmed;
  const isSiteRequired = checks?.isSiteRequired;
  const isSiteDisabled = checks?.siteStatus === DashboardChecksTypeEnum.Confirmed;
  const businessInfo = useAppSelector((state) => state.user.businessInfo);
  const businessDocuments = businessInfo?.documents;
  const FormInitialValues = {
    bNationalCode: businessInfo?.bNationalCode,
    businessDescription: businessInfo?.businessDescription,
    businessName: businessInfo?.businessName,
    economicCode: businessInfo?.economicCode,
    isOfficialSubmited: businessInfo?.isOfficialSubmited,
    sabtNumber: businessInfo?.sabtNumber,
    site: businessInfo?.site,
    taxCode: businessInfo?.taxCode,
  };

  const { navigateToLast } = useLastUrlWasPage();

  // =========================================
  // Handler
  // =========================================
  useMessagedAsync(() => dispatch(getUserChecks()), [dispatch], {
    useProgress: true,
  });

  const [, removeDocument] = useMessagedAsyncFn(
    (id) => fileApis.removeDocumentWithIdWithCancellationTokenDelete(id),
    [],
    undefined,
    {
      useProgress: true,
    },
  );

  const { loading } = useAsyncRetry(async () => {
    const { registrationDate: rawRegistrationDate, documents, ...other } = await dispatch(getUserBusinessinfo());

    const introLetterUrl =
      documents?.find((i) => i.fileType === FileTypeAdminEnum.IntroductionLetter)?.serverName ?? "";
    const businessCertUrl =
      documents?.find((i) => i.fileType === FileTypeAdminEnum.EstablishmentCertificate)?.serverName ?? "";
    const permissionUrl = documents?.find((i) => i.fileType === FileTypeAdminEnum.Mojavez)?.serverName ?? "";

    const introductionLetter = introLetterUrl?.split("/")[introLetterUrl.split("/").length - 1].split("?")[0];
    const businessCertificate = businessCertUrl?.split("/")[businessCertUrl.split("/").length - 1].split("?")[0];
    const permission = permissionUrl?.split("/")[permissionUrl.split("/").length - 1].split("?")[0];

    if (other) {
      const registrationDate = toJalali({
        convertTime: false,
        date: rawRegistrationDate,
        dateFormat: dateFormats.standard.utcDateTime,
        toFormat: dateFormats.standard.utcDate,
      }).object;

      form.setFieldsValue({
        ...other,
        introductionLetter: introductionLetter
          ? {
              file: {
                name: `${getFileName(introductionLetter)}`,
                size: 0,
                type: getFileType(introductionLetter),
                uid: "1",
                url: introLetterUrl,
              },
            }
          : undefined,
        businessCertificate: businessCertificate
          ? {
              file: {
                name: `${getFileName(businessCertificate)}`,
                size: 0,
                type: getFileType(businessCertificate),
                uid: "1",
                url: businessCertUrl,
              },
            }
          : undefined,
        permission: permission
          ? {
              file: {
                name: `${getFileName(permission)}`,
                size: 0,
                type: getFileType(permission),
                uid: "1",
                url: permissionUrl,
              },
            }
          : undefined,
        registrationDate,
      });
    }
  }, [dispatch, form]);

  const onStateChange = useCallback(
    (selectedState: string): void => {
      const stateData = location.filter((state) => state.stateName === selectedState);
      form.resetFields(["city"]);
      setCities(stateData[0]?.cities);
    },
    [form],
  );

  useEffect(() => {
    if (businessDocuments?.[0]) {
      const serverName = businessDocuments[0].serverName || "";
      const certificateName = serverName?.split("/")[serverName.split("/").length - 1].split("?")[0];
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setBusinessCertificateList([
        {
          name: `${getFileName(certificateName)}`,
          size: 0,
          type: getFileType(certificateName),
          uid: "1",
          url: serverName,
        },
      ]);
    }
    if (businessDocuments?.[1]) {
      const serverName = businessDocuments[1].serverName || "";
      const introductionLetterName = serverName?.split("/")[serverName.split("/").length - 1].split("?")[0];
      setIntroductionLetterList([
        {
          name: `${getFileName(introductionLetterName)}`,
          size: 0,
          type: getFileType(introductionLetterName),
          uid: "1",
          url: serverName,
        },
      ]);
    }
    if (businessDocuments?.[2]) {
      const permissionServerName = businessDocuments[2].serverName || "";
      const permissionName = permissionServerName?.split("/")[permissionServerName.split("/").length - 1].split("?")[0];
      setPermissionList([
        {
          name: `${getFileName(permissionName)}`,
          size: 0,
          type: getFileType(permissionName),
          uid: "1",
          url: permissionServerName,
        },
      ]);
    }
    return () => {
      setBusinessCertificateList([]);
      setIntroductionLetterList([]);
      setPermissionList([]);
    };
  }, [businessDocuments, form]);

  useEffect(() => {
    if (localInfo?.state) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      onStateChange(localInfo?.state);
    }
    form.setFieldsValue({
      address: localInfo?.address,
      city: localInfo?.city,
      localPhone: localInfo?.localPhone,
      postalCode: localInfo?.postalCode,
      state: localInfo?.state,
    });
  }, [dispatch, form, localInfo, onStateChange]);

  const [{ loading: loadingSubmit }, onSubmit] = useMessagedAsyncFn(
    async (values: BusinessInformation) => {
      const {
        businessCertificate,
        introductionLetter,
        permission,
        registrationDate,
        businessDescription,
        businessName,
        city,
        address,
        companyName,
        isOfficialSubmited,
        sabtNumber,
        site,
        taxCode,
        economicCode,
        postalCode,
        bNationalCode,
        localPhone,
      } = values;
      const documentsFileList = [
        ...(businessCertificate?.fileList || []),
        ...(introductionLetter?.fileList || []),
        ...(permission?.fileList || []),
      ];

      if (removeDocCalled && businessDocuments) {
        await Promise.all(
          businessDocuments.map(async (doc: UserDocumentAdminModel) => {
            await removeDocument(doc.id);
          }),
        );
      }
      if (documentsFileList) {
        await Promise.all(
          documentsFileList.map(async (doc: UploadFile) => {
            const fileName = doc.url ? doc.url.split("/").pop() : doc.response;
            await fileApis.addFileWithRequestModelWithCancellationTokenPost({
              fileName,
              type: FileTypeEnum.EstablishmentCertificate,
            });
          }),
        );
      }
      await dispatch(
        updateBusinessinfo({
          registrationDate: toUtc({
            convertTime: false,
            date: registrationDate,
            toFormat: dateFormats.standard.utcDate,
          }).string,
          businessDescription,
          businessName,
          economicCode,
          isOfficialSubmited,
          sabtNumber,
          site,
          taxCode,
          bNationalCode,
          companyName,
        }),
      );
      if (cities) {
        const cityData = cities.filter((c) => c.cityName === city);
        await userApis.localInformationsWithRequestModelWithCancellationTokenPut({
          cityId: cityData[0].cityId,
          localPhoneNumber: localPhone,
          postalCode,
          address,
        });
        navigate(DashboardRoutes.settings.business.root.url);
      }
      if (isRegisterURL) {
        navigate(DashboardRoutes.register.bank.url);
      } else {
        navigate(DashboardRoutes.settings.business.root.url);
      }
    },
    [removeDocCalled, businessDocuments, dispatch, cities, isRegisterURL, removeDocument, navigate],
  );

  const onChangeModalVisible = (visible: boolean) => {
    setModalVisible(visible);
  };

  // =========================================
  // Return
  // =========================================
  return (
    <Drawer
      title={isRegisterURL ? t("settings.business.paymentGatewayRequestForm") : t("settings.business.editForm")}
      open={true}
      closeIcon={false}
      height={"100%"}
      placement={"bottom"}
      extra={
        <Button
          type={"text"}
          icon={<Close width={14} height={14} />}
          onClick={() => {
            // TODO THIS MUST CHANGE
            navigate(settings.business.root.url);
          }}
        />
      }
    >
      <Page>
        <SubLayout>
          <FormLayout
            title={<Typography.Title level={4}>{t("settings.business.businessInformation")}</Typography.Title>}
            instructions={
              <>
                <p>{t("settings.business.instructions.businessName")}</p>
                <p>{t("settings.business.instructions.businessDescription")}</p>
                <p>{t("settings.business.instructions.postalAddress")}</p>
                <p>{t("settings.business.instructions.postalCodeAndHomePhoneNumber")}</p>
                <p>{t("settings.business.instructions.websiteAddress1")}</p>
                <p>{t("settings.business.instructions.websiteAddress2")}</p>
                <p>{t("settings.business.instructions.websiteAddress3")}</p>
                <p>{t("settings.business.instructions.websiteAddress4")}</p>
                <p>{t("settings.business.instructions.taxationCode1")}</p>
                <p>{t("settings.business.instructions.taxationCode2")}</p>
                <p>{t("settings.business.instructions.businessType")}</p>
                {businessInfo?.isOfficialSubmited && <p>{t("settings.business.instructions.officialBusiness")}</p>}
              </>
            }
          >
            <LegacyForm<BusinessInformation>
              initialValues={FormInitialValues}
              form={form}
              onFinish={onSubmit}
              scrollToFirstError
              layout={"vertical"}
            >
              <LegacyForm.Item
                rules={[
                  { required: true },
                  {
                    pattern: /^[\u0600-\u06FF\s]+$/,
                    message: t("common.validations.onlyPersianLetters"),
                  },
                ]}
                label={t("settings.business.businessName")}
                name="businessName"
              >
                <Input
                  placeholder={t("settings.business.placeholders.businessName")}
                  disabled={loading || (!form.getFieldValue("businessName") ? false : isBusinessDisabled)}
                />
              </LegacyForm.Item>
              <LegacyForm.Item
                label={t("settings.business.description")}
                name="businessDescription"
                rules={[{ required: true }, { min: 20 }]}
              >
                <Input.TextArea
                  placeholder={t("settings.business.placeholders.businessDescription")}
                  disabled={loading || (!form.getFieldValue("businessDescription") ? false : isBusinessDisabled)}
                />
              </LegacyForm.Item>
              <Row gutter={16}>
                <Col xs={24} lg={12}>
                  <LegacyForm.Item label={t("state")} rules={[{ required: true }]} name="state">
                    <Select
                      disabled={loading || (!form.getFieldValue("state") ? false : isLocalDisabled)}
                      onChange={onStateChange}
                    >
                      {location.map((state) => (
                        <Option value={state.stateName} key={state.stateId}>
                          {state.stateName}
                        </Option>
                      ))}
                    </Select>
                  </LegacyForm.Item>
                </Col>
                <Col xs={24} lg={12}>
                  <LegacyForm.Item
                    rules={[
                      { required: true },
                      {
                        validator(): Promise<void> {
                          if (form.getFieldValue("state")) return Promise.resolve();
                          return Promise.reject(new Error(t("settings.business.selectStateFirst")));
                        },
                      },
                    ]}
                    label={t("city")}
                    name="city"
                  >
                    <Select disabled={loading || (!form.getFieldValue("city") ? false : isLocalDisabled)}>
                      {cities?.map((city) => (
                        <Option value={city.cityName} key={city.cityId}>
                          {city.cityName}
                        </Option>
                      ))}
                    </Select>
                  </LegacyForm.Item>
                </Col>
              </Row>
              <LegacyForm.Item rules={[{ required: true }]} label={t("settings.business.postalAddress")} name="address">
                <Input.TextArea
                  placeholder={t("settings.business.placeholders.postalAddress")}
                  disabled={loading || (!form.getFieldValue("address") ? false : isLocalDisabled)}
                />
              </LegacyForm.Item>
              <Row gutter={16}>
                <Col xs={24} lg={12}>
                  <LegacyForm.Item
                    label={t("localPhone")}
                    name="localPhone"
                    rules={[
                      { required: true },
                      {
                        validator(_, value): Promise<void> {
                          if (value) {
                            if (ValidLocalPhoneNumber(value)) {
                              return Promise.resolve();
                            }
                            return Promise.reject(new Error(t("settings.business.errors.localPhone")));
                          }
                          return Promise.resolve();
                        },
                      },
                    ]}
                  >
                    <Input
                      disabled={loading || (!form.getFieldValue("localPhone") ? false : isLocalDisabled)}
                      maxLength={11}
                      placeholder={t("settings.business.placeholders.localPhone")}
                    />
                  </LegacyForm.Item>
                </Col>
                <Col xs={24} lg={12}>
                  <LegacyForm.Item
                    label={t("postalCode")}
                    name="postalCode"
                    rules={[
                      {
                        validator(_, value): Promise<void> {
                          if (ValidPostalCode(value)) {
                            return Promise.resolve();
                          }
                          return Promise.reject(new Error(t("settings.business.errors.postalCode")));
                        },
                      },
                      {
                        message: t("settings.business.errors.requiredForTaxCode", {
                          name: t("postalCode"),
                        }),
                        required: true,
                      },
                    ]}
                  >
                    <InputNumber
                      placeholder={t("settings.business.placeholders.postalCode")}
                      controls={false}
                      inputMode="numeric"
                      changeOnWheel={false}
                      className="tw:w-full!"
                      disabled={loading || (!form.getFieldValue("postalCode") ? false : isLocalDisabled)}
                    />
                  </LegacyForm.Item>
                </Col>
              </Row>
              <LegacyForm.Url
                form={form}
                className={"tw:dir-ltr!"}
                label={t("settings.business.websiteAddress")}
                rules={[
                  { required: isSiteRequired },
                  ({ getFieldValue }) => ({
                    validator(): Promise<void> {
                      const url = getFieldValue("site");
                      return url && !ValidUrl(url) ? Promise.reject(t("error.url")) : Promise.resolve();
                    },
                  }),
                ]}
                name={"site"}
                placeholder={"payping.ir"}
                disabled={loading || (!form.getFieldValue("site") ? false : isSiteDisabled)}
              />
              <LegacyForm.Item
                label={t("taxCode")}
                rules={[{ required: true }]}
                shouldUpdate={(prev, curr): boolean =>
                  prev.isOfficialSubmited !== curr.isOfficialSubmited || prev.taxCode !== curr.taxCode
                }
              >
                <Row gutter={[8, 8]}>
                  <Col xs={24}>
                    <LegacyForm.Item
                      noStyle
                      name="taxCode"
                      rules={[
                        () => ({
                          validator(_, value) {
                            if (!value || value.length === 10) {
                              return Promise.resolve();
                            }
                            return Promise.reject(new Error(t("settings.business.errors.taxCode")));
                          },
                        }),
                        { required: true },
                      ]}
                    >
                      <Input
                        disabled={loading}
                        placeholder={t("settings.business.placeholders.taxCode")}
                        maxLength={10}
                      />
                    </LegacyForm.Item>
                  </Col>
                </Row>
              </LegacyForm.Item>
              <LegacyForm.Item
                shouldUpdate={(prev, curr): boolean =>
                  prev.isOfficialSubmited !== curr.isOfficialSubmited || prev.taxCode !== curr.taxCode
                }
              >
                <Row className="tw:w-full" justify={"space-between"}>
                  <Button
                    icon={<InfoCircleOutlined width={14} height={14} />}
                    type={"text"}
                    onClick={() => onChangeModalVisible(true)}
                  >
                    {t("profile.business.getTaxCodeGuide")}
                  </Button>
                  <Modal
                    footer={null}
                    centered
                    closable
                    open={modalVisible}
                    className={classNames(styles.modalSize, "tw:p-8! tw:text-center")}
                    onCancel={() => onChangeModalVisible(false)}
                  >
                    <div className={styles.hIframeAparatEmbedFrame}>
                      <span className={"tw:block tw:pt-[57%]!"} />
                      <iframe
                        className="tw:rounded-[25px]"
                        src="https://www.aparat.com/video/video/embed/videohash/Qhbcq/vt/frame?&recom=self"
                        title={t("profile.tax.title")}
                        allowFullScreen={true}
                      />
                    </div>
                    <br />
                    <Typography.Link
                      target="_blank"
                      rel="noreferrer noopener"
                      href={buildUrl(URLS.PPNG_URL, "t/Nn8h").toString()}
                    >
                      {t("profile.tax.text")}
                    </Typography.Link>
                  </Modal>
                  <Button
                    type={"link"}
                    target="_blank"
                    rel="noreferrer noopener"
                    href="https://e3.tax.gov.ir/action/do/registrationselector"
                  >
                    {t("profile.business.taxCodeSystem")}
                  </Button>
                </Row>
              </LegacyForm.Item>
              <LegacyForm.Item valuePropName={"checked"} name={"isOfficialSubmited"}>
                <SwitchRow
                  label={t("settings.business.isOfficial")}
                  loading={loading}
                  // disabled={isBusinessDisabled}
                  checkedChildren={t("yes")}
                  unCheckedChildren={t("no")}
                />
              </LegacyForm.Item>
              <LegacyForm.Item
                noStyle
                shouldUpdate={(prev, curr): boolean => prev.isOfficialSubmited !== curr.isOfficialSubmited}
              >
                {(linkForm): ReactElement => {
                  const isOfficialSubmited = linkForm.getFieldValue("isOfficialSubmited");
                  return isOfficialSubmited ? (
                    <>
                      <LegacyForm.Item>
                        <Typography.Text className="tw:whitespace-pre-line" type={"secondary"}>
                          <Trans
                            i18nKey={"mobileEditPersonal.nameDesc"}
                            components={[
                              <Typography.Link
                                key={0}
                                href={"https://irsherkat.ssaa.ir/Design/SearchCompanyPublicInfo.aspx"}
                                target="_blank"
                              />,
                            ]}
                          />
                        </Typography.Text>
                      </LegacyForm.Item>
                      <LegacyForm.Item
                        rules={[{ required: true }]}
                        label={t("settings.business.officialBusinessName")}
                        name="companyName"
                      >
                        <Input
                          placeholder={t("settings.business.placeholders.officialBusinessName")}
                          disabled={loading || (!form.getFieldValue("companyName") ? false : isBusinessDisabled)}
                        />
                      </LegacyForm.Item>
                      <Row gutter={16}>
                        <Col xs={24} lg={12}>
                          <LegacyForm.Item
                            label={t("settings.business.registrationDate")}
                            name="registrationDate"
                            rules={[{ required: true }]}
                          >
                            <LegacyDatePicker
                              className={"tw:w-full"}
                              disabled={loading || (!form.getFieldValue("registrationDate") ? false : isSabtDisabled)}
                            />
                          </LegacyForm.Item>
                        </Col>
                        <Col xs={24} lg={12}>
                          <LegacyForm.Item
                            label={t("settings.business.sabtNumber")}
                            name="sabtNumber"
                            rules={[{ required: true }]}
                          >
                            <Input
                              placeholder={t("settings.business.placeholders.sabtNumber")}
                              disabled={loading || (!form.getFieldValue("sabtNumber") ? false : isSabtDisabled)}
                            />
                          </LegacyForm.Item>
                        </Col>
                      </Row>
                      <Row gutter={16}>
                        <Col xs={24} lg={12}>
                          <LegacyForm.Item
                            label={t("settings.business.bNationalCode")}
                            name="bNationalCode"
                            rules={[{ len: 11 }, { required: true }]}
                          >
                            <Input
                              placeholder={t("settings.business.placeholders.bNationalCode")}
                              disabled={loading || (!form.getFieldValue("bNationalCode") ? false : isBusinessDisabled)}
                            />
                          </LegacyForm.Item>
                        </Col>
                        <Col xs={24} lg={12}>
                          <LegacyForm.Item label={t("settings.business.economicCode")} name="economicCode">
                            <Input
                              placeholder={t("settings.business.placeholders.economicCode")}
                              disabled={loading || (!form.getFieldValue("economicCode") ? false : isBusinessDisabled)}
                            />
                          </LegacyForm.Item>
                        </Col>
                      </Row>
                      <Space orientation={"vertical"} className={"tw:w-full"} size={"middle"}>
                        <Typography.Text>
                          {t("settings.business.certificateOfEstablishmentAndLetterOfIntroduction")}
                        </Typography.Text>
                        <Row gutter={[24, 24]}>
                          <Col>
                            <LegacyForm.Item name={"businessCertificate"}>
                              <Upload
                                crop={false}
                                listType={"picture-card"}
                                requestFunction={uploadApis.miscWithFilePost}
                                onChange={(info: UploadChangeParam): void => setBusinessCertificateList(info.fileList)}
                                fileList={businessCertificateList}
                                showUploadList={{ showPreviewIcon: true, showRemoveIcon: true }}
                                onRemove={() => (isBusinessDisabled ? false : setRemoveDocCalled(true))}
                                allowedExtensions={["jpg", "jpeg", "png"]}
                              >
                                {businessCertificateList.length ? null : (
                                  <div>
                                    <UploadIcon width={"30px"} />
                                    <div className={"ant-upload-text"}>
                                      {t("settings.business.upload.certificateOfEstablishment")}
                                    </div>
                                  </div>
                                )}
                              </Upload>
                            </LegacyForm.Item>
                          </Col>
                          <Col>
                            <LegacyForm.Item name="introductionLetter">
                              <Upload
                                crop={false}
                                listType={"picture-card"}
                                requestFunction={uploadApis.miscWithFilePost}
                                onChange={(info: UploadChangeParam): void => setIntroductionLetterList(info.fileList)}
                                fileList={introductionLetterList}
                                showUploadList={{ showPreviewIcon: true, showRemoveIcon: true }}
                                onRemove={() => (isBusinessDisabled ? false : setRemoveDocCalled(true))}
                                allowedExtensions={["jpg", "jpeg", "png"]}
                              >
                                {introductionLetterList.length ? null : (
                                  <div>
                                    <UploadIcon width={"30px"} />
                                    <div className={"ant-upload-text"}>
                                      {t("settings.business.upload.introductionLetter")}
                                    </div>
                                  </div>
                                )}
                              </Upload>
                            </LegacyForm.Item>
                          </Col>
                          <Col>
                            <LegacyForm.Item name="permission">
                              <Upload
                                crop={false}
                                listType={"picture-card"}
                                requestFunction={uploadApis.miscWithFilePost}
                                onChange={(info: UploadChangeParam): void => setPermissionList(info.fileList)}
                                fileList={permissionList}
                                showUploadList={{ showPreviewIcon: true, showRemoveIcon: true }}
                                onRemove={() => (isBusinessDisabled ? false : setRemoveDocCalled(true))}
                                allowedExtensions={["jpg", "jpeg", "png"]}
                              >
                                {permissionList.length ? null : (
                                  <div>
                                    <UploadIcon width={"30px"} />
                                    <div className={"ant-upload-text"}>{t("settings.business.upload.permission")}</div>
                                  </div>
                                )}
                              </Upload>
                            </LegacyForm.Item>
                          </Col>
                        </Row>
                      </Space>
                    </>
                  ) : (
                    <Space orientation={"vertical"} className={"tw:w-full"} size={"middle"}>
                      <Typography.Text>
                        {t("settings.business.certificateOfEstablishmentAndLetterOfIntroduction")}
                      </Typography.Text>
                      <Row>
                        <LegacyForm.Item name="permission">
                          <Upload
                            crop={false}
                            listType={"picture-card"}
                            requestFunction={uploadApis.miscWithFilePost}
                            onChange={(info: UploadChangeParam): void => setPermissionList(info.fileList)}
                            fileList={permissionList}
                            showUploadList={{ showPreviewIcon: true, showRemoveIcon: true }}
                            onRemove={() => (isBusinessDisabled ? false : setRemoveDocCalled(true))}
                            allowedExtensions={["jpg", "jpeg", "png"]}
                          >
                            {permissionList.length ? null : (
                              <div>
                                <UploadIcon width={"30px"} />
                                <div className={"ant-upload-text"}>{t("settings.business.upload.permission")}</div>
                              </div>
                            )}
                          </Upload>
                        </LegacyForm.Item>
                      </Row>
                    </Space>
                  );
                }}
              </LegacyForm.Item>
              {isRegisterURL ? (
                <Row gutter={[8, 0]} className={"tw:w-full"}>
                  <Col span={11}>
                    <Button
                      htmlType={"submit"}
                      loading={loading || loadingSubmit}
                      className={"tw:w-full"}
                      type={"primary"}
                    >
                      {t("continue")}
                    </Button>
                  </Col>
                  <Col span={5}>
                    <Button
                      loading={loading || loadingSubmit}
                      className={"tw:w-full"}
                      onClick={() => {
                        navigateToLast();
                      }}
                    >
                      {t("return")}
                    </Button>
                  </Col>
                </Row>
              ) : (
                <Row gutter={[8, 0]}>
                  <Col span={11}>
                    <Button
                      className={"tw:w-full"}
                      type={"primary"}
                      loading={loading || loadingSubmit}
                      htmlType={"submit"}
                    >
                      {t("save")}
                    </Button>
                  </Col>
                  <Col span={5}>
                    <Button
                      loading={loading || loadingSubmit}
                      className={"tw:w-full"}
                      onClick={() => {
                        navigateToLast();
                      }}
                    >
                      {t("cancel")}
                    </Button>
                  </Col>
                </Row>
              )}
            </LegacyForm>
          </FormLayout>
        </SubLayout>
      </Page>
    </Drawer>
  );
};

export default BusinessEdit;
